import client from './client';
import { Coupon, CreateCouponInput, UpdateCouponInput } from '../types/coupon';

/**
 * The ecommerce service speaks discount_type / discount_value / is_active
 * (SQLite column names); the app uses type / value / active. This module is
 * the mapping boundary so page code never sees backend column names.
 */
function fromApi(row: any): Coupon {
  return {
    code: row.code,
    description: row.description ?? '',
    type: row.discount_type,
    value: row.discount_value,
    max_discount_value: row.max_discount_value ?? null,
    min_order_value: row.min_order_value ?? 0,
    max_uses: row.max_uses ?? null,
    used_count: row.used_count ?? 0,
    expires_at: row.expires_at ?? null,
    active: Boolean(row.is_active),
    visible: Boolean(row.is_public),
    in_banner: Boolean(row.show_in_banner),
    created_at: row.created_at,
  };
}

export async function getCoupons(): Promise<Coupon[]> {
  const res = await client.get('/api/admin/coupons');
  const raw = res.data;
  const rows =
    (Array.isArray(raw?.coupons) && raw.coupons) ||
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(raw) && raw) ||
    [];
  return rows.map(fromApi);
}

export async function createCoupon(data: CreateCouponInput): Promise<void> {
  await client.post('/api/admin/coupons', {
    code: data.code,
    description: data.description,
    discount_type: data.type,
    discount_value: data.value,
    // Only percentage coupons carry a cap; the API rejects it on fixed ones.
    max_discount_value: data.type === 'percentage' ? data.max_discount_value : null,
    min_order_value: data.min_order_value,
    max_uses: data.max_uses,
    expires_at: data.expires_at,
    is_active: data.active,
    is_public: data.visible,
    // The banner is only ever offered on a public coupon; sending it otherwise
    // is a 400 from the service, so mirror that rule here.
    show_in_banner: data.visible && data.in_banner,
  });
}

/**
 * Full edit of an existing coupon.
 *
 * `originalCode` addresses the row; `data.code` is what it should be called
 * afterwards, so the two differ on a rename. EVERY editable field is sent on
 * every save — the API treats a missing key as "leave it alone", so omitting
 * one would silently drop that edit. The three fields whose null is meaningful
 * (max_discount_value, max_uses, expires_at) are sent as explicit nulls rather
 * than left out, which is what makes "uncapped / unlimited / never expires"
 * reachable from the form at all.
 */
export async function updateCoupon(originalCode: string, data: UpdateCouponInput): Promise<Coupon> {
  const res = await client.put(`/api/admin/coupons/${encodeURIComponent(originalCode)}`, {
    code: data.code,
    description: data.description,
    discount_type: data.type,
    discount_value: data.value,
    max_discount_value: data.type === 'percentage' ? data.max_discount_value : null,
    min_order_value: data.min_order_value,
    max_uses: data.max_uses,
    expires_at: data.expires_at,
    is_active: data.active,
    is_public: data.visible,
    show_in_banner: data.visible && data.in_banner,
  });
  return fromApi(res.data?.coupon ?? res.data);
}

export async function toggleCoupon(code: string, active: boolean): Promise<void> {
  await client.put(`/api/admin/coupons/${encodeURIComponent(code)}`, { is_active: active });
}

/**
 * Change where a coupon is advertised.
 *
 * Both flags go on every call rather than only the one that changed: turning
 * visibility off must take the coupon out of the banner too, and stating the
 * pair outright is what stops a half-applied edit leaving a private code
 * scrolling across the landing page.
 */
export async function setCouponVisibility(
  code: string,
  visible: boolean,
  inBanner: boolean,
): Promise<void> {
  await client.put(`/api/admin/coupons/${encodeURIComponent(code)}`, {
    is_public: visible,
    show_in_banner: visible && inBanner,
  });
}

export async function deleteCoupon(code: string): Promise<void> {
  await client.delete(`/api/admin/coupons/${encodeURIComponent(code)}`);
}
